import rootGetters from '../getters';
import rootSetters from '../actions';
import { formatDateHour, mergeSizes } from './utils';
import {
  getCampaignItems,
  getDynamics,
  getLayoutItems,
  getOrientationItems,
  getSizeItems,
} from './getters';
import { getForm, getShowSingleForm } from '../form/getters';
import * as queryTypes from './graphqlTypes';
import { ApolloQueryResult } from '@apollo/client';
import * as lodash from 'lodash';
import { defaultBannerForm } from '../form/utils';
import { setBatchForm } from '../form/actionCreators';
import PdfBanner from '../../components/Modal/PrintBannersModal/PdfBanner';
import { margin } from '../../components/Modal/PrintBannersModal/utils';
import { PDFDocument } from 'pdf-lib';

export const updateBannerLayout =
  () => (dispatch: DispatchType, getState: GetStateType) => {
    const {
      form: { setForm },
    } = rootSetters(dispatch);

    const dynamics = getDynamics(getState())();
    const selectedDynamic = getShowSingleForm(getState())('model.dynamic');
    const existingDynamicIndex = dynamics.findIndex(
      ({ id }) => id === selectedDynamic,
    );

    if (existingDynamicIndex === -1) {
      dispatch(setBatchForm('model.dynamic', (dynamics[0]?.id ?? '') as any));
    }

    const sizes = getSizeItems(getState())();
    const selectedSize = getShowSingleForm(getState())('model.size');
    const existingSizeIndex = sizes.findIndex(({ id }) => id === selectedSize);

    if (existingSizeIndex === -1) {
      dispatch(setBatchForm('model.size', (sizes[0]?.id ?? '') as any));
    }

    const orientations = getOrientationItems(getState())();
    const selectedOrientation =
      getShowSingleForm(getState())('model.orientation');
    const existingOrientationIndex = orientations.findIndex(
      ({ id }) => id === selectedOrientation,
    );

    if (existingOrientationIndex === -1) {
      dispatch(setBatchForm('model.orientation', orientations[0]?.id ?? ''));
    }

    const layouts = getLayoutItems(getState())();
    const selectedLayout = getShowSingleForm(getState())('model.layout');
    const existingLayoutIndex = layouts.findIndex(
      ({ id }) => id === selectedLayout,
    );
    if (existingLayoutIndex === -1) {
      dispatch(setBatchForm('model.layout', layouts[0]?.id ?? ''));
    }

    const campaigns = getCampaignItems(getState())();
    const selectedCampaign = getShowSingleForm(getState())('model.campaign');
    const existingCampaignIndex = campaigns.findIndex(
      ({ id }) => id === selectedCampaign,
    );
    if (existingCampaignIndex === -1) {
      dispatch(setBatchForm('model.campaign', campaigns[0]?.id ?? ''));
    }
    dispatch(updateUserSiganature());
  };

export const updateUserSiganature =
  () => (dispatch: DispatchType, getState: GetStateType) => {
    const {
      user: { getSelectedFilial },
      banner: { getBanner },
    } = rootGetters(getState());

    const {
      form: { setBatchForm },
    } = rootSetters(dispatch);

    setBatchForm(
      'price.releases.0',
      `${getBanner('signature')} - ${formatDateHour(
        new Date(),
        ' - ',
      )} - ${getSelectedFilial().fantasyName.toUpperCase()}`,
    );
  };

export const setBannerConfigs =
  () => (dispatch: DispatchType, getState: GetStateType) => {
    const {
      user: { getUser },
    } = rootGetters(getState());

    const {
      banner: { setBanner, updateBannerLayout },
    } = rootSetters(dispatch);

    const configs = getUser('current_user.clients.0.banner') as Banner;

    const dynamics = configs.layouts.reduce((acc, acv) => {
      acv.dynamics.forEach((dynamic) => {
        acc[dynamic.name] = dynamic.description;
      });
      return acc;
    }, {});

    setBanner({
      field: 'dynamics',
      value: dynamics as Dynamics,
    });

    setBanner({
      field: 'configs',
      value: configs,
    });

    const sizesObject = {};
    mergeSizes(configs.layouts).forEach((size) => {
      const { name, width, height } = size;
      sizesObject[name] = { width, height };
    });

    setBanner({
      field: 'sizes',
      value: sizesObject as Record<SizeType, Size>,
    });

    updateBannerLayout();
  };

export const sendFormBanner =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<any>((resolve, reject) => {
      const {
        form: { getForm },
        banner: { getBanner },
      } = rootGetters(getState());
      const {
        banner: { setBanner, createFormBanner, updateFormBanner },
      } = rootSetters(dispatch);

      let newBatchBanner = lodash.merge({}, getForm('batchBanner'));

      newBatchBanner = {
        ...newBatchBanner,
        batch: {
          ...newBatchBanner.batch,
          availableProducts: [],
        },
      };

      if (!getBanner('isEditMode')) {
        createFormBanner(newBatchBanner)
          .then((result) => {
            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        updateFormBanner(getBanner('editFormBannerId'), newBatchBanner)
          .then((result) => {
            setBanner({
              field: 'editFormBannerId',
              value: 0,
            });
            setBanner({
              field: 'isEditMode',
              value: false,
            });
            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      }
    });

export const createFormBanner =
  (form: BannerForm) => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<any>((resolve, reject) => {
      const {
        user: { getUser },
      } = rootGetters(getState());
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      MUTATE<CreateFormBannerMutation>({
        mutation: queryTypes.CREATE_FORM_BANNER,
        variables: {
          createFormInput: {
            userId: getUser('current_user.id'),
            bannerId: getUser('current_user.clients.0.banner.id'),
            content: form,
          },
        },
      })
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const updateFormBanner =
  (id: number, form: BannerForm) => (dispatch: DispatchType) =>
    new Promise<any>((resolve, reject) => {
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      MUTATE<UpdateFormBannerMutation>({
        mutation: queryTypes.UPDATE_FORM_BANNER,
        variables: {
          id,
          updateFormBannerInput: {
            content: form,
          },
        },
      })
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const filterFormBanners =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FilterFormBannersQuery>>(
      (resolve, reject) => {
        const {
          graphql: { QUERY },
          banner: { setBanner },
        } = rootSetters(dispatch);
        const {
          banner: { getBanner, getBannerId },
          user: { getUserId },
        } = rootGetters(getState());
        const searchFilter = getBanner('filters.form.filter');
        QUERY<FilterFormBannersQuery>({
          query: queryTypes.FILTER_FORM_BANNERS,
          variables: {
            bannerId: getBannerId(),
            userId: getUserId(),
            searchFilter,
          },
        })
          .then((result) => {
            const data = result.data.filterFormBanners;
            setBanner({
              field: 'filters.form.data',
              value: data,
            });
            setBanner({
              field: 'formCheck',
              value: data.data.map(
                (_, index) => !!getBanner(`formCheck.${index}`) || false,
              ),
            });
            setBanner({
              field: 'formQuantity',
              value: data.data.map(
                (_, index) => getBanner(`formQuantity.${index}`) || 1,
              ),
            });

            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      },
    );

export const autocompleteBannerForms =
  (field: keyof BannerState['autocompletes']['form']) =>
  (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<string[]>((resolve, reject) => {
      const {
        banner: {
          autocompletes: { form },
        },
      } = getState();
      const {
        graphql: { QUERY },
        banner: { setBanner },
      } = rootSetters(dispatch);
      const {
        banner: { getBanner, getBannerId },
        user: { getUserId },
      } = rootGetters(getState());

      QUERY<AutocompleteFormBannersQuery>({
        query: queryTypes.AUTOCOMPLETE_FORM_BANNERS,
        variables: {
          bannerId: getBannerId(),
          userId: getUserId(),
          autocompleteFilter: form[field].filter,
        },
      })
        .then((result) => {
          const data = result.data.autocompleteFormBanners;
          setBanner({
            field: `autocompletes.form.${field}.data`,
            value: data,
          });
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const findOneFormBanner =
  (formBannerId: number) => (dispatch: DispatchType) =>
    new Promise<ApolloQueryResult<FindOneFormBannerQuery>>(
      (resolve, reject) => {
        const {
          graphql: { QUERY },
        } = rootSetters(dispatch);

        QUERY<FindOneFormBannerQuery>({
          query: queryTypes.FIND_ONE_FORM_BANNER,
          variables: {
            id: formBannerId,
          },
        })
          .then((result) => {
            resolve({
              ...result,
              data: {
                ...result.data,
                FindOneFormBanner: {
                  ...result.data.FindOneFormBanner,
                  content: lodash.merge(
                    {},
                    defaultBannerForm(),
                    result.data.FindOneFormBanner.content,
                  ),
                },
              },
            });
          })
          .catch((error) => {
            reject(error);
          });
      },
    );

export const findManyFormBanners =
  (formBannerIds: number[]) => (dispatch: DispatchType) =>
    new Promise<ApolloQueryResult<FindManyFormBannersQuery>>(
      (resolve, reject) => {
        const {
          graphql: { QUERY },
        } = rootSetters(dispatch);

        QUERY<FindManyFormBannersQuery>({
          query: queryTypes.FIND_MANY_FORM_BANNER,
          variables: {
            ids: formBannerIds,
          },
        })
          .then((result) => {
            resolve({
              ...result,
              data: {
                ...result.data,
                FindManyFormBanners: result.data.FindManyFormBanners.map(
                  (formBanner) => ({
                    ...formBanner,
                    content: lodash.merge(
                      {},
                      defaultBannerForm(),
                      formBanner.content,
                    ),
                  }),
                ),
              },
            });
          })
          .catch((error) => {
            reject(error);
          });
      },
    );

export const editFormBanner =
  (formBannerId: number) => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<void>((resolve, reject) => {
      const {
        form: { setForm },
        banner: { findOneFormBanner, setBanner },
      } = rootSetters(dispatch);

      const {
        form: { getForm },
        banner: { getBanner, getDefaultBannerForm },
      } = rootGetters(getState());

      findOneFormBanner(formBannerId)
        .then((response) => {
          const { id, content } = response.data.FindOneFormBanner;
          let newBatchBanner = lodash.merge(
            {},
            getDefaultBannerForm(),
            content,
          );
          setBanner({
            field: 'editFormBannerId',
            value: id,
          });
          setBanner({
            field: 'isEditMode',
            value: true,
          });
          if (!getBanner('isEditMode'))
            setBanner({
              field: 'savedForm',
              value: getForm('batchBanner'),
            });
          setForm('batchBanner', newBatchBanner);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });

export const addSingleBannerFormsToPrint =
  (bannerForms: (BannerForm & { quantity?: number })[]) =>
  (dispatch: DispatchType, getState: GetStateType) => {
    const {
      banner: { getDefaultBannerForm },
    } = rootGetters(getState());
    const {
      banner: { setBanner },
    } = rootSetters(dispatch);

    const defaultForm = getDefaultBannerForm();
    let newSingleForms: SingleBannerForm[] = [];
    for (const {
      singleForms,
      defaultSingleForm,
      quantity = 1,
    } of bannerForms) {
      newSingleForms = newSingleForms.concat(
        (singleForms.length > 0 ? singleForms : [defaultSingleForm])
          .map((singleForm) => lodash.merge({}, defaultForm, singleForm))
          .map((singleForm) =>
            lodash.merge(singleForm, {
              config: {
                printer: {
                  copies: singleForm.config.printer.copies * quantity,
                },
              },
            }),
          ),
      );
    }
    setBanner({ field: 'singleBannerFormsToPrint', value: newSingleForms });
  };

export const visualizeFormBanner =
  (formBannerId: number) => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<void>((resolve, reject) => {
      const {
        banner: { getBanner },
      } = rootGetters(getState());

      const {
        modal: { openSingleModal },
        banner: { findOneFormBanner, addSingleBannerFormsToPrint },
      } = rootSetters(dispatch);

      findOneFormBanner(formBannerId)
        .then((response) => {
          const forms = getBanner('filters.form.data.data');
          const bannerForm = response.data.FindOneFormBanner;
          const formBannerIndex = forms.findIndex(
            ({ id }) => id === bannerForm.id,
          );
          const quantity = getBanner(`formQuantity.${formBannerIndex}`);
          addSingleBannerFormsToPrint([{ ...bannerForm.content, quantity }]);
          openSingleModal('printBannerForm');
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });

export const visualizeFormBanners =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<void>((resolve, reject) => {
      const {
        banner: { getBanner },
      } = rootGetters(getState());
      const {
        modal: { openSingleModal },
        banner: { findManyFormBanners, addSingleBannerFormsToPrint },
      } = rootSetters(dispatch);

      const forms = getBanner('filters.form.data.data');
      const formCheck = getBanner('formCheck');
      const ids = forms
        .filter((_, index) => formCheck[index])
        .map(({ id }) => id);

      findManyFormBanners(ids)
        .then((response) => {
          const bannerForms = response.data.FindManyFormBanners;
          const replacedBannerForms = bannerForms.map((bannerForm) => {
            const formBannerIndex = forms.findIndex(
              ({ id }) => id === bannerForm.id,
            );
            const quantity = getBanner(`formQuantity.${formBannerIndex}`);
            return { ...bannerForm.content, quantity };
          });
          addSingleBannerFormsToPrint(replacedBannerForms);
          openSingleModal('printBannerForm');
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });

export const cancelEditFormBanner =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<void>((resolve) => {
      const {
        banner: { setBanner },
        form: { setForm },
      } = rootSetters(dispatch);
      const {
        banner: { getBanner },
      } = rootGetters(getState());
      setBanner({
        field: 'isEditMode',
        value: false,
      });
      setBanner({
        field: 'editFormBannerId',
        value: 0,
      });
      setForm('batchBanner', getBanner('savedForm'));
      resolve();
    });

export const deleteManyFormBanners =
  (ids: number[]) => (dispatch: DispatchType) =>
    new Promise<any>((resolve, reject) => {
      const {
        graphql: { MUTATE },
        banner: { setBanner },
      } = rootSetters(dispatch);
      setBanner({
        field: 'status.deleteFormBanners',
        value: 'pending',
      });
      MUTATE<any>({
        mutation: queryTypes.DELETE_MANY_FORM_BANNERS,
        variables: {
          ids,
        },
      })
        .then((result) => {
          setBanner({
            field: 'status.deleteFormBanners',
            value: 'fulfilled',
          });
          resolve(result);
        })
        .catch((error) => {
          setBanner({
            field: 'status.deleteFormBanners',
            value: 'rejected',
          });
          reject(error);
        });
    });

export const deleteSelectedFormBanners =
  () => (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<any>((resolve, reject) => {
      const {
        banner: { getBanner },
      } = rootGetters(getState());
      const {
        banner: { deleteManyFormBanners },
      } = rootSetters(dispatch);
      const forms = getBanner('filters.form.data.data');
      const formCheck = getBanner('formCheck');

      const ids = forms
        .filter((_, index) => formCheck[index])
        .map(({ id }) => id);

      deleteManyFormBanners(ids)
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const setBannerStatus =
  (status: keyof BannerState['status'], value: STATUS) =>
  (dispatch: DispatchType, getState: GetStateType) => {
    const {
      banner: { setBanner },
    } = rootSetters(dispatch);

    setBanner({
      field: `status.${status}`,
      value,
    });
  };

export const applyFormFragment =
  (selectedDynamic: keyof Dynamics) =>
  (dispatch: DispatchType, getState: GetStateType) => {
    const {
      form: { getForm },
      banner: { getFormFragment },
    } = rootGetters(getState());

    const {
      form: { setForm },
    } = rootSetters(dispatch);

    setForm(
      'banner',
      lodash.mergeWith(
        {},
        getForm('banner'),
        getFormFragment(selectedDynamic),
        (_, b) => (lodash.isArray(b) ? [...b] : undefined),
      ),
    );
  };

export const renderForms =
  (
    forms: SingleBannerForm[],
    backgroundImages: {
      url: string;
      bytes: ArrayBuffer;
    }[],
    fonts: {
      fontFamily: string;
      fontWeight: string;
      buffer: ArrayBuffer;
    }[],
    enabledBackground: boolean,
    xOldPriceBytes: ArrayBuffer,
  ) =>
  async (_, getState: GetStateType) => {
    const {
      banner: { getBanner, getBackgroundImage },
      form: { getBannerConfigurations, getTransformations },
    } = rootGetters(getState());
    const designedForms: {
      form: SingleBannerForm;
      pdfDocument: PDFDocument;
      pageWidth: number;
      pageHeight: number;
    }[] = [];

    const sizes = getBanner('sizes');
    for (let formIndex in forms) {
      const form = forms[formIndex];
      const { size, orientation } = form.model;

      const pageWidth =
        sizes[size][orientation === 'landscape' ? 'height' : 'width'] * 2.83467;

      const { height: pageHeight } = getBannerConfigurations(pageWidth, form)!;
      const pngImageBytes = backgroundImages.find(
        ({ url }) => url === getBackgroundImage(form),
      )?.bytes;
      const elements = getTransformations(pageWidth, form, 'pdf');
      const pdfBanner = await new PdfBanner().create(
        [pageWidth, pageHeight],
        fonts,
        pngImageBytes,
        enabledBackground,
        xOldPriceBytes,
      );

      for (let element of elements) {
        switch (element.elementType) {
          case 'price':
            await pdfBanner.priceNode({
              ...element,
              price: +element.text,
              margin: margin(element),
            });
            break;
          case 'qrcode':
            await pdfBanner.qrcodeNode(element);
            break;
          case 'barcode':
            await pdfBanner.barcodeNode(element);
            break;
          case 'economize':
            await pdfBanner.economize(element);
            break;
          case 'validity':
            await pdfBanner.validity(element);
            break;
          default:
            await pdfBanner.textNode(element);
            break;
        }
      }

      const pdfDocument = pdfBanner.pdfDoc!;
      await pdfDocument.save();

      designedForms.push({
        form,
        pdfDocument,
        pageWidth,
        pageHeight,
      });
    }

    return designedForms;
  };

const bannerActionCreators = (dispatch: <T>(action: any) => T) =>
  Object.assign(
    {},
    {
      updateBannerLayout: () => dispatch<void>(updateBannerLayout()),
      setBannerConfigs: () => dispatch<void>(setBannerConfigs()),
      createFormBanner: (form: BannerForm) =>
        dispatch<Promise<any>>(createFormBanner(form)),
      filterFormBanners: () => dispatch<Promise<any>>(filterFormBanners()),
      findOneFormBanner: (formBannerId: number) =>
        dispatch<Promise<ApolloQueryResult<FindOneFormBannerQuery>>>(
          findOneFormBanner(formBannerId),
        ),
      findManyFormBanners: (formBannerIds: number[]) =>
        dispatch<Promise<ApolloQueryResult<FindManyFormBannersQuery>>>(
          findManyFormBanners(formBannerIds),
        ),
      editFormBanner: (formBannerId: number) =>
        dispatch<Promise<void>>(editFormBanner(formBannerId)),
      visualizeFormBanner: (formBannerId: number) =>
        dispatch<Promise<void>>(visualizeFormBanner(formBannerId)),
      visualizeFormBanners: () =>
        dispatch<Promise<void>>(visualizeFormBanners()),
      cancelEditFormBanner: () =>
        dispatch<Promise<void>>(cancelEditFormBanner()),
      deleteManyFormBanners: (ids: number[]) =>
        dispatch<Promise<void>>(deleteManyFormBanners(ids)),
      deleteSelectedFormBanners: () =>
        dispatch<Promise<void>>(deleteSelectedFormBanners()),
      updateFormBanner: (id: number, form: BannerForm) =>
        dispatch<Promise<any>>(updateFormBanner(id, form)),
      sendFormBanner: () => dispatch<Promise<any>>(sendFormBanner()),
      setBannerStatus: (status: keyof BannerState['status'], value: STATUS) =>
        dispatch<Promise<any>>(setBannerStatus(status, value)),
      updateUserSiganature: () => dispatch<void>(updateUserSiganature()),
      autocompleteBannerForms: (
        field: keyof BannerState['autocompletes']['form'],
      ) => dispatch<Promise<string[]>>(autocompleteBannerForms(field)),
      applyFormFragment: (selectedDynamic: keyof Dynamics) =>
        dispatch<Promise<string[]>>(applyFormFragment(selectedDynamic)),
      addSingleBannerFormsToPrint: (
        bannerForms: (BannerForm & { quantity?: number })[],
      ) => dispatch<void>(addSingleBannerFormsToPrint(bannerForms)),
      renderForms: (
        forms: SingleBannerForm[],
        backgroundImages: {
          url: string;
          bytes: ArrayBuffer;
        }[],
        fonts: {
          fontFamily: string;
          fontWeight: string;
          buffer: ArrayBuffer;
        }[],
        enabledBackground: boolean,
        xOldPriceBytes: ArrayBuffer,
      ) =>
        dispatch<
          {
            form: SingleBannerForm;
            pdfDocument: PDFDocument;
            pageWidth: number;
            pageHeight: number;
          }[]
        >(
          renderForms(
            forms,
            backgroundImages,
            fonts,
            enabledBackground,
            xOldPriceBytes,
          ),
        ),
    },
  );
export default bannerActionCreators;
// export const addSingleBannerFormsToPrint =
//   (bannerForms: BannerForm[]) =>
